import {Injectable, Injector, Inject, Input, EventEmitter} from "@angular/core";
import {Subject} from "rxjs/Subject";
import {Observable} from "rxjs/Observable";
import {MediatorService} from './mediator.service';
import {MediatorEventLookup} from './mediator-event-lookup.service';
import {HydUser} from "../models/user";
import {HydrogenAppConfigService} from '../host.config.service';
import { LogService } from './log.service';





/**
 * When SignalR runs it will add functions to the global $ variable 
 * that you use to create connections to the hub. However, in this
 * class we won't want to depend on any global variables, so this
 * class provides an abstraction away from using $ directly in here.
 */
export class SignalrWindow extends Window {
    $: any;
}

export enum ConnectionState {
    Connecting = 1,
    Connected = 2,
    Reconnecting = 3,
    Disconnected = 4
}

class Device {
    id: string;
    cid: string;
    AccountId: string;
    MACAddress: string;
    Name: string;
    GroupId: string;
    UserPrincipalNames: string[];
    Status: string;
    ShowNotification: Boolean;
    objectEnum: ObjectType;
}

class Group {
    GroupID: string;
    ParentGroupID: string;
    GroupName: string;
    ActionType: string;
    AccountId: string;
    ShowNotification: Boolean;
    objectEnum: ObjectType;


}


enum ObjectType {
    Device,
    Group,
    License,
    User,
    Policy

}




/**
 * ChannelService is a wrapper around the functionality that SignalR
 * provides to expose the ideas of channels and events. With this service
 * you can subscribe to specific channels (or groups in signalr speak) and
 * use observables to react to specific events sent out on those channels.
 */
@Injectable()
export class SignalRService {

    /**
     * starting$ is an observable available to know if the signalr 
     * connection is ready or not. On a successful connection this
     * stream will emit a value.
     */
    starting$: Observable<any>;

    /**
     * connectionState$ provides the current state of the underlying
     * connection as an observable stream.
     */
    connectionState$: Observable<ConnectionState>;

    /**
     * error$ provides a stream of any error messages that occur on the 
     * SignalR connection
     */
    error$: Observable<string>;

    // These are used to feed the public observables 
    //
    private connectionStateSubject = new Subject<ConnectionState>();
    private startingSubject = new Subject<any>();
    private errorSubject = new Subject<any>();

    // These are used to track the internal SignalR state 
    //
    private hubConnection: any;
    private hubProxy: any;

    injector: Injector;

    // An internal array to track what channel subscriptions exist 
    //


    constructor(
        @Inject(SignalrWindow) private window: SignalrWindow,
        private _mediator: MediatorService,
        private _hydUser: HydUser,
        private configSvc: HydrogenAppConfigService,
        private _logService: LogService,
        injector: Injector
    ) {
        this.injector = injector;
        if (typeof this.window.$ === undefined || typeof this.window.$.hubConnection === undefined) {
            throw new Error("The variable '$' or the .hubConnection() function are not defined...please check the SignalR scripts have been loaded properly");
        }

        //MM - Not needed yet for Dashboard applications
        //this.connect();

    }




    private connect() {

        this.connectionState$ = this.connectionStateSubject.asObservable();
        this.error$ = this.errorSubject.asObservable();
        this.starting$ = this.startingSubject.asObservable();

        this.hubConnection = this.window.$.hubConnection();
        this.hubConnection.url = this.configSvc.config.serviceUrl.signalr + '/signalr';
        this.hubConnection.logging = false;
        this.hubConnection.EnableDetailedErrors = true;
        this.hubProxy = this.hubConnection.createHubProxy('DeviceNotificationHub');



        this.hubProxy.on("UpdateDeviceStatus", (device: Device) => {

            // console.log('RTN Event Received for Device cid:' + device.cid + ' AccountID: ' + device.AccountId);

            if (device.objectEnum == ObjectType.License) {
                this._mediator.Action(MediatorEventLookup.LicenseStatusUpdate, device);
            } else if (device.objectEnum == ObjectType.Device && device.Status == "MoveDevices") {

                //Refresh the portal for group drag and drop and dropd a device.
                this._mediator.Action(MediatorEventLookup.DeviceMoved, device);
            }
            else {

                //device status update
                this._mediator.Action(MediatorEventLookup.DeviceStatusUpdate, device);

                //Group settings Update
                if (device.GroupId != null)
                    this._mediator.Action(MediatorEventLookup.GroupSettingsUpdateSelected, device)

                if (device.GroupId != null && (device.Status == "ONLINE" || device.Status == "OFFLINE"))
                    this._mediator.Action(MediatorEventLookup.GroupStatusUpdateSelected, device)
            }
        });

        this.hubProxy.on("UpdateGroups", (group: Group) => {

            // console.log('RTN Event Received for Group id:' + group.GroupID + ' and AccountID: ' + group.AccountId);

           
                 //Refresh the portal for group tree drag and drop beween groups.
            if (group.ActionType == "MoveGroup"){ //group.objectEnum == ObjectType.Group) {

                this._mediator.Action(MediatorEventLookup.GroupMoved, group);

            } else {
                 //Update Group Tree node for creating new group/updating group name/deleting group
                this._mediator.Action(MediatorEventLookup.GroupUpdate, group);
                //bug no:144755-Before we didn't handle RTN if group dosen't have any devcies, now this issue got fixed by calling below method.
                this._mediator.Action(MediatorEventLookup.GroupSettingsUpdateSelected, group.GroupID);
            }
        });

        // Define handlers for the connection state events
        //
        this.hubConnection.stateChanged((state: any) => {

            this._hydUser = this.injector.get(HydUser);
            this.hubConnection.qs = { "accountId": this._hydUser.AccountId, "isSource": "false", "token": this._hydUser.AccessToken.replace('bearer ', '') };

            let newState = ConnectionState.Connecting;

            switch (state.newState) {
                case this.window.$.signalR.connectionState.connecting:
                    newState = ConnectionState.Connecting;
                    break;
                case this.window.$.signalR.connectionState.connected:
                    newState = ConnectionState.Connected;
                    break;
                case this.window.$.signalR.connectionState.reconnecting:
                    newState = ConnectionState.Reconnecting;
                    this.reconnecting({ hydUser: this._hydUser });
                    break;
                case this.window.$.signalR.connectionState.disconnected:
                    newState = ConnectionState.Disconnected;
                    this.disconnected({ hydUser: this._hydUser });
                    break;
            }

            // Push the new state on our subject
            //
            this.connectionStateSubject.next(newState);
        });

        // Define handlers for any errors
        this.hubConnection.error((error: any) => {
            // Push the error on our subject
            this.errorSubject.next(error);
        });

        if (this._hydUser.UserId == null) {
            this._hydUser.AuthorizationObservable.subscribe(
                data => {
                    this.start();
                },
                err => console.error(err),
                () => { });
        } else {
            this.start();
        }



        this.hubConnection.reconnecting(function () {
            console.log('Reconnecting SignalR');  // Your function to notify user.
        });
    }


    /**
     * Start the SignalR connection. The starting$ stream will emit an 
     * event if the connection is established, otherwise it will emit an
     * error.
     */
    private start(): void {

        this.hubConnection.qs = { "accountId": this._hydUser.AccountId, "isSource": "false", "token": this._hydUser.AccessToken.replace('bearer ', '') };
        this.hubConnection.start()
            .done(() => {
                console.log('SignalR connected, connection ID=' + this.hubConnection.id);

            })
            .fail((error: any) => {
                console.log('SignalR Could not connect');

            });
    }

    private disconnected(obj) {
        console.log('SignalR hub disconnected');
        var hydUser = obj.hydUser;
        var sigRsvc = this;
        //sigRsvc.connect();
        setTimeout(function () {
            sigRsvc.connect();
        }, 5000); // Restart connection after 5 seconds.
    }

    private reconnecting(obj) {
        console.log('SignalR hub reconnecting');
        var hydUser = obj.hydUser;
        var sigRsvc = this;
        sigRsvc.hubConnection.disconnected();
        //sigRsvc.connect();
        setTimeout(function () {
            sigRsvc.connect();
        }, 5000); // Restart connection after 5 seconds.
    }

}


// WEBPACK FOOTER //
// C:/WS/Fusion/FusionDashboard/src/app/services/signalR.service.ts